import { useAuth } from '../utils/hooks'
import { PropsWithChildren, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  authenticatedDisallowedRoutes,
  ROUTE,
  RoutePermissions,
} from '../cnst/router.cnst'
import FullScreenLoader from '../components/FullScreenLoader'

const RouteGuardProvider = ({ children }: PropsWithChildren) => {
  const { user, isAuthenticating } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const pathname = useMemo(() => {
    let pathname = location.pathname.toLowerCase()
    if (pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1)
    }
    return pathname as ROUTE
  }, [location.pathname])

  const requiredPermissions = useMemo(() => {
    return RoutePermissions[pathname] || []
  }, [pathname])

  const userExists = useMemo(() => {
    return !!user
  }, [user])

  const disalowAuthenticated = useMemo(() => {
    return authenticatedDisallowedRoutes.includes(pathname)
  }, [pathname])

  const userHasRequiredPermissions = useMemo(() => {
    if (!user) return false
    return requiredPermissions.every(
      (permission) =>
        user.permissionsV2 && user.permissionsV2.includes(permission)
    )
  }, [user, requiredPermissions])

  const shouldRedirectToHome = useMemo(() => {
    return disalowAuthenticated && userExists
  }, [disalowAuthenticated, userExists])

  const shouldRedirectToLogin = useMemo(() => {
    return !disalowAuthenticated && !userHasRequiredPermissions
  }, [disalowAuthenticated, userHasRequiredPermissions])

  const shouldRenderChildren = useMemo(() => {
    if (isAuthenticating) return false
    return userHasRequiredPermissions || !requiredPermissions.length
  }, [isAuthenticating, userHasRequiredPermissions, requiredPermissions])

  useEffect(() => {
    if (isAuthenticating) return
    if (shouldRedirectToHome) return void navigate(ROUTE.DASHBOARD)
    if (shouldRedirectToLogin) return void navigate(ROUTE.LOGIN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticating, shouldRedirectToHome, shouldRedirectToLogin])

  if (shouldRenderChildren) return children
  return <FullScreenLoader />
}

export default RouteGuardProvider
